/**
 * BillingCallback  (/billing/callback)
 * ─────────────────────────────────────────────────────────────────────────────
 * Paystack redirects here after checkout with ?reference= (and ?trxref=).
 * We verify the transaction server-side and show the outcome. The webhook is the
 * real source of truth, so verify() is just the user-facing confirmation — and
 * it's idempotent, so a double-settle can't happen.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import { verifyPayment } from "@/lib/billing";

type State = "verifying" | "success" | "failed";

const BillingCallback = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const reference = params.get("reference") || params.get("trxref") || "";

  const [state, setState] = useState<State>("verifying");
  const [detail, setDetail] = useState<string>("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard against StrictMode double-run
    ran.current = true;

    if (!reference) {
      setState("failed");
      setDetail("No payment reference was provided.");
      return;
    }

    verifyPayment(reference)
      .then((res) => {
        if (res.status === "success") {
          setState("success");
          const until = res.subscription_end_date
            ? new Date(res.subscription_end_date).toLocaleDateString()
            : null;
          setDetail(until ? `Your plan is active until ${until}.` : "Your plan is now active.");
        } else {
          setState("failed");
          setDetail("The payment was not completed. You haven't been charged for an incomplete payment.");
        }
      })
      .catch((err) => {
        console.error("[billing] verify failed:", err);
        setState("failed");
        setDetail("We couldn't confirm this payment. If you were charged, it'll be applied automatically shortly.");
      });
  }, [reference]);

  return (
    <div className="min-h-screen bg-console-bg text-console-text font-geist flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md bg-console-surface border border-console-line rounded-[12px] p-8 text-center space-y-5">
        <div className="flex justify-center">
          <Logo tone="text" size="md" />
        </div>

        {state === "verifying" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-console-primary animate-spin" />
            <h1 className="font-geist font-semibold text-[20px] tracking-tight">Confirming your payment…</h1>
            <p className="font-geist text-[14px] text-console-muted">Hang tight, this only takes a moment.</p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle2 className="h-10 w-10 mx-auto text-console-ok" />
            <h1 className="font-geist font-semibold text-[20px] tracking-tight">Payment confirmed</h1>
            <p className="font-geist text-[14px] text-console-muted">{detail}</p>
            <Button onClick={() => navigate("/host-dashboard")}
              className="w-full h-12 text-[14px] font-semibold font-geist bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]">
              Go to dashboard
            </Button>
          </>
        )}

        {state === "failed" && (
          <>
            <XCircle className="h-10 w-10 mx-auto text-console-red" />
            <h1 className="font-geist font-semibold text-[20px] tracking-tight">Payment not completed</h1>
            <p className="font-geist text-[14px] text-console-muted">{detail}</p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => navigate("/host-dashboard")}
                className="flex-1 h-12 text-[14px] font-medium font-geist text-console-text hover:bg-white/5 border border-console-line-strong rounded-[6px]">
                Back to dashboard
              </Button>
              <Button onClick={() => navigate("/subscription")}
                className="flex-1 h-12 text-[14px] font-semibold font-geist bg-console-primary text-console-bg hover:bg-console-primary/90 rounded-[6px]">
                Try again
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingCallback;
